import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Divider,
  IconButton,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import SettingsIcon from '@mui/icons-material/Settings';

import { FragmentType, graphql, useFragment } from '../../__generated__';
import CountryFlag from '../../components/common/CountryFlag';
import { flagCodes } from '../../util/options';

const ITEM_HEIGHT = 48;

export const Tenant_RowFragment = graphql(/* GraphQL */ `
  fragment Tenant_RowFragment on Tenant {
    name
    code
    id
    type
    commission
    maxBonus
    duplicateThreshold
    rateChangeThreshold1
    rateChangeThreshold2
    rateChangeThreshold3
    currency
    logo
    receiptLabel
    receiptLineOne
    receiptLineTwo
    receiptFootnote
    status
  }
`);

export type PlatformClientRowProps = {
  tenant: FragmentType<typeof Tenant_RowFragment>;
  setSelectedTenant: (tenant: FragmentType<typeof Tenant_RowFragment>) => void;
  canEdit: boolean;
  onEdit: () => void;
};

const PlatformClientRow = (props: PlatformClientRowProps) => {
  const tenant = useFragment(Tenant_RowFragment, props.tenant);
  const countryCode: string = flagCodes[tenant.currency];

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    props.setSelectedTenant(props.tenant);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const onOpenEdit = () => {
    handleClose();
    props.onEdit();
  };

  const onClickViewDetails = () => {
    handleClose();
    if (tenant.id) navigate(`/platform-clients/details/${tenant.id}`);
  };

  return (
    <React.Fragment key={tenant.id}>
      <ListItem
        alignItems="flex-start"
        secondaryAction={
          <React.Fragment>
            <IconButton
              edge="end"
              aria-label="more"
              id="long-button"
              aria-controls={open ? 'long-menu' : undefined}
              aria-expanded={open ? 'true' : undefined}
              aria-haspopup="true"
              onClick={(e) => handleClick(e)}
              color={'primary'}
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="long-menu"
              MenuListProps={{
                'aria-labelledby': 'long-button',
              }}
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: -80,
              }}
              PaperProps={{
                style: {
                  maxHeight: ITEM_HEIGHT * 4.5,
                  width: '15ch',
                },
              }}
            >
              {props.canEdit && (
                <MenuItem key={'edit'} onClick={onOpenEdit}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  Edit
                </MenuItem>
              )}
              <MenuItem key={'details'} onClick={onClickViewDetails}>
                <ListItemIcon>
                  <SettingsIcon fontSize="small" />
                </ListItemIcon>
                Details
              </MenuItem>
            </Menu>
          </React.Fragment>
        }
      >
        <ListItemAvatar>
          <CountryFlag
            countryCode={countryCode}
            otherProps={{
              style: {
                fontSize: '3em',
                lineHeight: '3em',
              },
            }}
          />
        </ListItemAvatar>
        <ListItemText
          primary={tenant.name}
          secondary={
            <React.Fragment>
              <Typography
                sx={{ display: 'inline' }}
                component="span"
                variant="body2"
                color="text.primary"
              >
                Bonus Limit:{' '}
              </Typography>
              <Typography
                sx={{ display: 'inline', fontWeight: 'bold' }}
                component="span"
                variant="subtitle2"
                color="text.primary"
              >
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: tenant.currency,
                }).format(tenant.maxBonus)}
              </Typography>
            </React.Fragment>
          }
        />
      </ListItem>
      <Divider variant="inset" component="li" />
    </React.Fragment>
  );
};

export default PlatformClientRow;
